import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translate';
import './Guide.css';

// 辅助函数：根据语言返回文本
const getText = (language, jaText, enText, zhText) => {
  if (language === 'ja') return jaText;
  if (language === 'zh_tw') return zhText;
  return enText; // 默认英语
};

const Guide = () => {
  const location = useLocation();
  const [activeGuide, setActiveGuide] = useState('use');
  const { language } = useLanguage();

  const guideLinks = [
    { id: 'license', path: '/guide?page=license', label: t('guide.license', language), active: activeGuide === 'license' },
    { id: 'use', path: '/guide?page=use', label: t('guide.usage', language), active: activeGuide === 'use' },
    { id: 'safe', path: '/guide?page=safe', label: t('guide.safety', language), active: activeGuide === 'safe' },
  ];

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const page = searchParams.get('page');
    if (page === 'license' || page === 'use' || page === 'safe') {
      setActiveGuide(page);
    } else {
      setActiveGuide('use');
    }
  }, [location.search]);

  const handleGuideClick = (id) => {
    setActiveGuide(id);
  };

  const renderContent = () => {
    if (activeGuide === 'license') {
      return (
        <>
          <p className="name">{getText(language, '国際運転免許証', 'International Driver\'s License', '國際駕駛執照')}</p>
          <div className="title">
            <p className="name">{getText(language, '国際運転免許証の利用について', 'About International Driver\'s License Usage', '國際駕駛執照使用說明')}</p>
          </div>
          <div className="txt">
            <p>{getText(language, 'ジュネーブ条約（1949）発行の国際運転免許証が利用できます。', 'International driver\'s licenses issued under the Geneva Convention (1949) can be used.', '可使用日內瓦公約（1949）簽發的國際駕駛執照。')}</p>
            <p>{getText(language, '利用時は、有効なパスポートと、お住まいの国の公認機関より取得した有効な国際運転免許証の掲示が必須です。', 'When using, a valid passport and a valid international driver\'s license obtained from an authorized institution in your country of residence must be presented.', '使用時必須出示有效護照及從居住國官方機構取得的有效國際駕駛執照。')}</p>
            <p>{getText(language, '国際運転免許証の有効期限は発行から1年、日本での運転有効期限は入国日より1年（パスポートの日本国入国年月日のスタンプにより確認）です。ただし、日本の住民基本台帳に記録されている方が、日本を出国後3カ月に満たない期間内に再入国した場合は、その再入国の日は「上陸した日（日本での運転有効期限の起算日）」にはなりません。', 'The validity period of an international driver\'s license is 1 year from issuance, and the driving validity period in Japan is 1 year from the date of entry (confirmed by the entry stamp in your passport). However, if a person recorded in Japan\'s Basic Resident Register re-enters Japan within less than 3 months after leaving Japan, the date of re-entry is not considered the \'date of landing\' (the starting date of driving validity in Japan).', '國際駕駛執照有效期為發行後1年，在日本駕駛有效期為入境日起1年（以護照日本入境日期戳確認）。但若在日本居民基本台帳有記錄者，於離開日本後未滿3個月內再次入境時，該再次入境日不視為「登陸日（日本駕駛有效期起算日）」。')}</p>
            <p>{getText(language, 'また、パリ条約、ワシントン条約、ウィーン条約加盟国で取得した国際運転免許証での運転は、日本国内では認められていません。', 'Additionally, driving with an international driver\'s license obtained from countries party to the Paris Convention, Washington Convention, or Vienna Convention is not permitted in Japan.', '此外，使用巴黎公約、華盛頓公約、維也納公約成員國簽發的國際駕駛執照在日本境內駕駛不被認可。')}</p>
          </div>
          <div className="title">
            <p className="name">{getText(language, '外国運転免許証の利用について', 'About Foreign Driver\'s License Usage', '外國駕駛執照使用說明')}</p>
          </div>
          <div className="txt">
            <p>{getText(language, '対象国および地域：スイス連邦・ドイツ連邦共和国・フランス共和国・ベルギー王国・モナコ公国の5カ国と台湾，発行の外国運転免許証は利用できます。', 'Target countries and regions: Foreign driver\'s licenses issued from 5 countries (Swiss Confederation, Federal Republic of Germany, French Republic, Kingdom of Belgium, Principality of Monaco) and Taiwan can be used.', '適用國家及地區：瑞士聯邦、德意志聯邦共和國、法蘭西共和國、比利時王國、摩納哥公國等5國及台灣簽發的外國駕駛執照可使用。')}</p>
            <p>{getText(language, '利用時には、免許証の日本語翻訳文、およびパスポートの同時提示が必要です。', 'When using, a Japanese translation of the license and simultaneous presentation of a passport are required.', '使用時需同時出示駕駛執照的日文翻譯件及護照。')}</p>
          </div>
          <div className="txt">
            <p>{getText(language, '日本語翻訳文については、以下の機構が発行したもののみ有効です。', 'Only Japanese translations issued by the following organizations are valid.', '日文翻譯件僅限以下機構簽發者有效。')}</p>
            <p>{getText(language, '日本での運転有効期限は、上記の外国運転免許証の有効期限内かつ、入国日より1年（パスポートの日本国入国年月日のスタンプにより確認）です。ただし、日本の住民基本台帳に記録されている方が、日本を出国後3カ月に満たない期間内に再入国した場合は、その再入国の日は「上陸した日（日本での運転有効期限の起算日）」にはなりません。', 'The driving validity period in Japan is within the validity period of the above foreign driver\'s license and 1 year from the date of entry (confirmed by the entry stamp in your passport). However, if a person recorded in Japan\'s Basic Resident Register re-enters Japan within less than 3 months after leaving Japan, the date of re-entry is not considered the \'date of landing\' (the starting date of driving validity in Japan).', '在日本駕駛有效期為上述外國駕駛執照有效期內，且自入境日起1年（以護照日本入境日期戳確認）。但若在日本居民基本台帳有記錄者，於離開日本後未滿3個月內再次入境時，該再次入境日不視為「登陸日（日本駕駛有效期起算日）」。')}</p>
          </div>
        </>
      );
    }

    if (activeGuide === 'safe') {
      return (
        <>
          <p className="name">{getText(language, '保険・補償', 'Insurance & Coverage', '保險與保障')}</p>
          <div className="title">
            <p className="name">{getText(language, '地域によって保険・補償内容が異なることがあります。', 'Insurance and coverage contents may vary by region.', '保險與保障內容可能因地區而異。')}</p>
          </div>
          <table className="guide-table2" border="1">
            <tbody>
              <tr>
                <th>{getText(language, '夏季', 'Summer', '夏季')}</th>
                <th></th>
                <th>{getText(language, '基本コース（円）', 'Basic Course（円）', '基本方案（日圓）')}</th>
                <th>{getText(language, '安心コース（円）', 'Peace of Mind Course（円）', '安心方案（日圓）')}</th>
              </tr>

              {/* 事故发生时的负担金 */}
              <tr>
                <td rowSpan="5">
                  {getText(language, <>事故発生時の<br />負担金（円）</>, <>Burden at<br />Accident（円）</>, <>事故發生時<br />負擔金額（日圓）</>)}
                </td>
                <td>{getText(language, '加入料金', 'Enrollment Fee', '加入費用')}</td>
                <td>0</td>
                <td>{getText(language, '3,500/日', '3,500/day', '3,500/日')}</td>
              </tr>
              <tr>
                <td>
                  {getText(language, <>搭乗者<br/>傷害保険<br />最高30,000,000</>, <>Passenger Injury<br />Insurance<br />Max30,000,000</>, <>乘客<br/>傷害保險<br />最高30,000,000</>)}
                </td>
                <td>0</td>
                <td>0</td>
              </tr>
              <tr>
                <td>{getText(language, 'TPL 第三者損害賠償免責額', 'TPL Third Party Liability Deductible', 'TPL 第三方損害賠償免責額')}</td>
                <td>50,000</td>
                <td>0</td>
              </tr>
              <tr>
                <td>{getText(language, 'CDW 車損害補償免責額', 'CDW Vehicle Damage Compensation Deductible', 'CDW 車輛損害補償免責額')}</td>
                <td>{getText(language, '50,000–300,000', '50,000-300,000', '50,000–300,000')}</td>
                <td>0</td>
              </tr>

              <tr>
                <td>{getText(language, 'NOC 休業補償免責額', 'NOC Business Interruption Compensation Deductible', 'NOC 營業中斷補償免責額')}</td>
                <td>50,000</td>
                <td>0</td>
              </tr>

              {/* 事故发生时的赔偿金 */}
              <tr>
                <td rowSpan="4">
                  {getText(language, <>事故発生時の<br />賠償金額（円）</>, <>Compensation at<br />Accident（円）</>, <>事故發生時<br />賠償金額（日圓）</>)}
                </td>
                {/* <td>{getText(language, '鍵紛失', 'Key Loss', '鑰匙遺失')}</td>
                <td>{getText(language, '50,000~', '50,000~', '50,000~')}</td>
                <td>0</td> */}
              </tr>
              <tr>
                <td>{getText(language, 'レッカー', 'Tow Truck', '拖吊車')}</td>
                <td>{getText(language, '30,000~', '30,000~', '30,000~')}</td>
                <td>0</td>
              </tr>
              <tr>
                <td>
                  {getText(language, <>ライト、フロントガラス、<br />ガラスとドアミラー</>, <>Lights, Windshield,<br />Glass and Door Mirrors</>, <>車燈、擋風玻璃、<br />玻璃與車門後視鏡</>)}
                </td>
                <td>{getText(language, '50,000~', '50,000~', '50,000~')}</td>
                <td>0</td>
              </tr>
              <tr>
                <td>
                  {getText(language, '車両の底部およびルームの損傷', 'Vehicle Bottom and Interior Damage', '車輛底部及車室損傷')}
                </td>
                <td>{getText(language, '50,000~', '50,000~', '50,000~')}</td>
                <td>0</td>
              </tr>
            </tbody>
          </table>
          <table className="guide-table2" border="1">
            <tbody>
              <tr>
                <th>{getText(language, '冬季', 'Winter', '冬季')}</th>
                <th></th>
                <th>{getText(language, '基本コース（円）', 'Basic Course（円）', '基本方案（日圓）')}</th>
                <th>{getText(language, '安心コース（円）', 'Peace of Mind Course（円）', '安心方案（日圓）')}</th>
              </tr>

              {/* 事故发生时的负担金 */}
              <tr>
                <td rowSpan="5">
                  {getText(language, <>事故発生時の<br />負担金（円）</>, <>Burden at<br />Accident（円）</>, <>事故發生時<br />負擔金額（日圓）</>)}
                </td>
                <td>{getText(language, '加入料金', 'Enrollment Fee', '加入費用')}</td>
                <td>0</td>
                <td>{getText(language, '3,900/日', '3,900/day', '3,900/日')}</td>
              </tr>
              <tr>
                <td>
                  {getText(language, <>搭乗者<br />傷害保険<br />最高30,000,000</>, <>Passenger Injury<br />Insurance<br />Max30,000,000</>, <>乘客<br />傷害保險<br />最高30,000,000</>)}
                </td>
                <td>0</td>
                <td>0</td>
              </tr>
              <tr>
                <td>{getText(language, 'TPL 第三者損害賠償免責額', 'TPL Third Party Liability Deductible', 'TPL 第三方損害賠償免責額')}</td>
                <td>100,000</td>
                <td>0</td>
              </tr>
              <tr>
                <td>{getText(language, 'CDW 車損害補償免責額', 'CDW Vehicle Damage Compensation Deductible', 'CDW 車輛損害補償免責額')}</td>
                <td>300,000</td>
                <td>0</td>
              </tr>

              <tr>
                <td>{getText(language, 'NOC 休業補償免責額', 'NOC Business Interruption Compensation Deductible', 'NOC 營業中斷補償免責額')}</td>
                <td>150,000</td>
                <td>0</td>
              </tr>

              {/* 事故发生时的赔偿金 */}
              <tr>
                <td rowSpan="4">
                  {getText(language, <>事故発生時の<br />賠償金額（円）</>, <>Compensation at<br />Accident（円）</>, <>事故發生時<br />賠償金額（日圓）</>)}
                </td>
                {/* <td>{getText(language, '鍵紛失', 'Key Loss', '鑰匙遺失')}</td>
                <td>{getText(language, '50,000~', '50,000~', '50,000~')}</td>
                <td>0</td> */}
              </tr>
              <tr>
                <td>{getText(language, 'レッカー', 'Tow Truck', '拖吊車')}</td>
                <td>{getText(language, '30,000~', '30,000~', '30,000~')}</td>
                <td>0</td>
              </tr>
              <tr>
                <td>
                  {getText(language, <>ライト、フロントガラス、<br />ガラスとドアミラー</>, <>Lights, Windshield,<br />Glass and Door Mirrors</>, <>車燈、擋風玻璃、<br />玻璃與車門後視鏡</>)}
                </td>
                <td>{getText(language, '50,000~', '50,000~', '50,000~')}</td>
                <td>0</td>
              </tr>
              <tr>
                <td>
                  {getText(language, '車両の底部およびルームの損傷', 'Vehicle Bottom and Interior Damage', '車輛底部及車室損傷')}
                </td>
                <td>{getText(language, '50,000~', '50,000~', '50,000~')}</td>
                <td>0</td>
              </tr>
            </tbody>
          </table>
          <div className="title">
            <p className="name">{getText(language, 'このような場合は、保険・補償が適用されません', 'In the following cases, insurance and coverage do not apply', '以下情況保險與保障不適用')}</p>
          </div>
          <div className="guide-line">
            <p className="txt3">
              <span className="name3">{getText(language, '下記の状況は保険約款の免責事項に該当します', 'The above situations fall under insurance policy exclusions', '以下情況屬於保險條款免責事項')}</span>
              <span className="content">
                {getText(language, <>(1) パンクまたはバーストによるタイヤの損傷<br />(2) ホイールキャップの紛失<br />(3) 故意による事故<br />(4) 車内設備の人的損傷<br />(5) 燃料の誤給油または燃料切れ<br />(6) 動物との衝突（動物に衝突した場合、動物から衝突された場合を含む） など<br /></>, <>(1) Tire damage due to puncture or burst<br />(2) Loss of wheel caps<br />(3) Intentional accidents<br />(4) Human damage to vehicle interior equipment<br />(5) Wrong fuel type or fuel exhaustion<br />(6) Collision with animals (including when hitting animals or being hit by animals) etc.<br /></>, <>(1) 輪胎因刺破或爆裂損壞<br />(2) 輪轂蓋遺失<br />(3) 故意造成的事故<br />(4) 車內設備人為損壞<br />(5) 加錯燃油或燃油耗盡<br />(6) 與動物碰撞（包括撞到動物或被動物撞擊）等<br /></>)}
              </span>
            </p>
          </div>
          <div className="guide-line">
            <p className="txt3">
              <span className="name3">{getText(language, '貸渡約款に違反する事由が明らかになった場合', 'When violations of rental terms are discovered', '發現違反租賃條款情況時')}</span>
              <span className="content">
                {getText(language, <>(1) 飲酒運転、薬物使用<br />(2) 無謀運転<br />(3) 無断延長<br />(4) 当社の承認を受けていない運転者 など<br /></>, <>(1) Drunk driving, drug use<br />(2) Reckless driving<br />(3) Unauthorized extension<br />(4) Drivers not approved by our company etc.<br /></>, <>(1) 酒後駕駛、藥物使用<br />(2) 魯莽駕駛<br />(3) 未經授權延長<br />(4) 未經本公司批准的駕駛者等<br /></>)}
              </span>
            </p>
          </div>
          <div className="guide-line">
            <p className="txt3">
              <span className="name3">{getText(language, '管理上の落ち度があった場合', 'When there are management oversights', '存在管理疏忽時')}</span>
              <span className="content">
                {getText(language, <>(1) 車内装備品への損害<br />(2) 車両盗難<br />(3) セルフ給油による油種間違い<br />(4) 鍵紛失<br />(5) 車内での喫煙（電子、加熱式タバコ含む）<br />(6) 危険運転及びその他の迷惑行為 など<br /></>, <>(1) Damage to vehicle interior equipment<br />(2) Vehicle theft<br />(3) Wrong fuel type due to self-refueling<br />(4) Key loss<br />(5) Smoking in the vehicle (including electronic and heated tobacco)<br />(6) Dangerous driving and other nuisance behaviors etc.<br /></>, <>(1) 車內設備損壞<br />(2) 車輛被盜<br />(3) 自助加油導致油種錯誤<br />(4) 鑰匙遺失<br />(5) 車內吸煙（包括電子煙、加熱煙草）<br />(6) 危險駕駛及其他滋擾行為等<br /></>)}
              </span>
            </p>
          </div>
        </>
      );
    }

    // Default to 'use' guide
    return (
      <>
        <p className="name">{getText(language, 'ご利用方法', 'How to Use', '使用方法')}</p>
        <div className="title">
          <p className="name">{getText(language, '予約方法', 'Reservation Method', '預約方法')}</p>
        </div>
        <div className="guide-line">
          <p className="txt4" style={{ margin: '0 20px' }}>
            {getText(language, '予約方法：レンタカーの予約は、当社と提携している大手旅行会社Klookにてご予約頂けます。', 'Reservation Method: Car hire reservations can be made through Klook, a major travel agency with which we have a partnership agreement.', '預約方式：可透過合作平台Klook進行線上預約，或聯繫我司客戶服務。')}
          </p>
        </div>
        <div className="title">
          <p className="name">{getText(language, '出発前', 'Before Departure', '出發前')}</p>
        </div>
        <div className="guide-line">
          <p className="txt5">
            <span className="name3">{getText(language, 'つながる', 'Connect', '聯繫')}</span>
            <span className="content">
              {getText(language, 'Instagram/WhatsApp/LINEで弊社のサービスセンターに連絡してください。ご予約を照会します。', 'Please contact our service center via Instagram/WhatsApp/LINE. We will check your reservation.', '聯繫方式：線上可透過Instagram/Whatsapp/LINE 聯繫本公司的客戶服務，即時為您提供合適預訂方案！')}
            </span>
          </p>
        </div>
        <div className="guide-line">
          <p className="txt5">
            <span className="name3">{getText(language, '必要なものを用意する', 'Prepare Required Items', '準備所需物品')}</span>
            <span className="content">
              {getText(language, '有効な運転免許証（運転者全員の免許証が必要）', 'Valid driver\'s license (required for all drivers)', '有效駕駛執照（所有駕駛者均需提供）')}
            </span>
            <span className="name3">{getText(language, '1.日本人のお客様 ', '1. Japanese Customers', '1. 日本籍客戶')}</span>
            <span className="content" style={{ width: '92%', padding: '0 4%' }}>
              {getText(language, '日本の運転免許証の提示が必要です。', 'Japanese driving licence must be presented.', '出示日本駕駛執照。')}
            </span>
            <span className="name3">{getText(language, '2.日本の運転免許証をお持ちの外国籍のお客様 ', '2. Foreign nationals holding a Japanese driving licence', '2. 持有日本駕駛執照的外國籍客戶')}</span>
            <span className="content" style={{ width: '92%', padding: '0 4%' }}>
              {getText(language, '日本の運転免許証の提示が必要です。', 'Japanese driving licence must be presented.', '需出示日本駕駛執照及日本在留卡。')}
            </span>
            <span className="name3">{getText(language, '3.外国籍のお客様', '3. Foreign Customers', '3. 外國籍客戶')}</span>
            <span className="content" style={{ width: '92%', padding: '0 4%' }}>
              {getText(language, 'ジュネーブ条約加盟国発行の国際免許証の場合は、パスポートの提示が必要です。対象国および地域発行の外国運転免許証の場合は、免許証の日本語翻訳文とパスポートの提示が必要です。', 'For international licenses issued by Geneva Convention member countries, passport presentation is required. For foreign driver\'s licenses issued by target countries and regions, a Japanese translation of the license and passport presentation are required.', '若為日內瓦公約成員國簽發的國際駕駛執照，需出示護照。若為適用國家及地區簽發的外國駕駛執照，需出示駕駛執照的日文翻譯件及護照。')}
            </span>
            <Link to="/guide?page=license" className="content" style={{ width: '92%', padding: '10px 4% 0 4%', color: '#fe4a00', textDecoration: 'underline' }}>
              {getText(language, '国際運転免許証の利用について', 'About International Driver\'s License Usage', '國際駕駛執照使用說明')}
            </Link>
          </p>
        </div>
        <div className="title">
          <p className="name">{getText(language, '出発手続き', 'Departure Procedure', '出發前手續')}</p>
        </div>
        <div className="guide-line">
          <p className="txt5">
            <span className="name3">{getText(language, '指定の出発場所に行く', 'Go to Departure Location', '在預約的門店指定的位置取車')}</span>
            <span className="content">
              {getText(language, 'ご予定時間に指定の出発場所にお越しください。', 'Please arrive at the designated departure location at your scheduled time.', '請與預定時間內抵達指定門店。')}
            </span>
          </p>
        </div>
        <div className="guide-line">
          <p className="txt5">
            <span className="name3">{getText(language, '説明を聞く', 'Listen to Explanation', '提取車說明')}</span>
            <span className="content">
              {getText(language, '自動車貸渡証を作成します。「補償内容」「事故時の対応負担金」「その他の注意事項」を説明します。', 'We will create a vehicle rental certificate. We will explain "coverage details", "accident response fees", and "other precautions".', '我們將提供車輛租賃合同；並說明「車保險的細節內容」、「發生事故應對及需承擔的責任範圍」及「其他車輛注意事項」。')}
            </span>
          </p>
        </div>
        <div className="guide-line">
          <p className="txt5">
            <span className="name3">{getText(language, '車のチェック', 'Vehicle Check', '車輛交付檢查')}</span>
            <span className="content">
              {getText(language, '利用する車の外装、タイヤ、車内をご確認いただきます。車の操作方法、給油口の開け方や装備品などに関して、ご不明点等あれば、店舗スタッフにお尋ねください。', 'Please check the exterior, tires, and interior of the vehicle you will be using. Also, if you have any questions about vehicle operation, how to open the fuel filler, or equipment, please ask the store staff.', <>請檢查您將使用的車輛外觀、輪胎及車內衛生狀況。<br />如有任何關於車輛操作、加油的開啟方式或其他設備問題，請現場諮詢店鋪工作人員。</>)}
            </span>
          </p>
        </div>
        <div className="title">
          <p className="name">{getText(language, 'ご利用中', 'During Use', '租賃使用期間')}</p>
        </div>
        <div className="guide-line">
          <p className="txt5">
            <span className="name3">{getText(language, '事故が起きた場合の対応', 'Response in Case of Accident', '如發生事故時')}</span>
            <span className="content">
              {getText(language, 
                 <>1.負傷者の救護をしてください。 <br />2.事故現場から警察へ連絡(110番)をしてください。 <br />3.続いてご利用店舗へ連絡をしてください。万が一の時にも安心してご利用いただけるよう、安心の補償プランをご用意しております。</>,
                 <>1. Provide first aid to injured persons. <br />2. Please contact the police (dial 110) from the scene of the accident.<br /> 3. Then contact the rental store. We have peace of mind coverage plans so you can use our service with confidence even in case of emergencies.</>,
                 <>1.請在事故現場聯繫警察（撥打110）<br />2.如有人員傷亡，即時報警及對傷者進行適當救援。<br />3.聯繫租車門店，會有專員提供救援服務！</>)}
            </span>
            <Link to="/guide?page=safe" className="content" style={{ width: '100%', padding: '10px 0 0 0', color: '#fe4a00', textDecoration: 'underline' }}>
              {getText(language, '保険・補償制度について', 'About Insurance & Coverage System', '保險與保障制度說明')}
            </Link>
          </p>
        </div>
        <div className="guide-line">
          <p className="txt5">
            <span className="name3">{getText(language, '駐車違反時の対応', 'Response to Parking Violations', '違例泊車時')}</span>
            <span className="content">
              {getText(language, 'レンタカーをご利用中に放置駐車違反があった場合、お客様の責任において、その地域を管轄する警察署に出頭して所定の手続きを完了し、直ちに反則金の納付を完了してください。ご返却時に交通反則告知書と領収印のある納付書・領収証書等の書類を、確認のため営業所員にご提示ください。ご提示いただけない場合（反則金の納付が確認できない場合）は、25,000円（駐車違反金）を頂戴いたします。', 'If you receive a parking violation while using a rental car, you are responsible for appearing at the police station that has jurisdiction over the area, completing the required procedures, and immediately paying the fine. When returning the vehicle, please present the traffic violation notice and payment receipt with receipt stamp to the office staff for confirmation. If you cannot present these documents (if payment of the fine cannot be confirmed), we will collect 25,000 yen (parking violation fee).', '若在使用車輛期間發生違例泊車產生罰單，您需要按照罰單上提供的警察局地址辦理手續及繳納款項。 歸還車輛時，請向營業員出示及提供違例通知書及繳費收據等文件以供租賃公司留存。若無法出示相關文件（無法確認是否繳納的情況）需收取25,000日圓。')}
            </span>
          </p>
        </div>
        <div className="title">
          <p className="name">{getText(language, 'ご返却', 'Return', '還車')}</p>
        </div>
        <div className="guide-line">
          <p className="txt5">
            <span className="name3">{getText(language, '返却場所に行く', 'Go to Return Location', '前往歸還地點')}</span>
            <span className="content">
              {getText(language, 'ご予定の時間までに指定の返却場所にお越しください。ご予定を変更される場合や、返却時間に間に合わない場合は、返却時間までに、ご出発の営業所にご連絡いただき、承認を得てください。', 'Please arrive at the designated return location by your scheduled time. If you need to change your schedule or cannot make it in time for the return, please contact the departure office in advance and obtain approval.', 
                <>請於預定時間前到達指定的歸還地點。<br />如需變更還車時間或無法歸還，請提前聯繫出發營業所！</>)}
            </span>
          </p>
        </div>
        <div className="guide-line">
          <p className="txt5">
            <span className="name3">{getText(language, '給油', 'Refueling', '加油')}</span>
            <span className="content">
              {getText(language, 'ご返却の際は、燃料を満タンにすること、また、給油の領収書の提出が必要です。ご都合により満タンで返却できない場合は、燃料ゲージの残量による精算をさせていただきます。この場合、実際の給油金額より割高となりますので、あらかじめご了承ください。', 'Please refuel to full when returning the vehicle. Also, you will need the refueling receipt when returning. If you cannot return the vehicle with a full tank due to circumstances, we will calculate based on the remaining fuel gauge. In this case, please note that it will be more expensive than the actual refueling cost.',
                 <>還車時需要加滿燃料，並交付加油收據給工作人員。 <br />若因故無法加滿歸還，我們將根據實際的油量表剩餘的油量進行結算。 此情況下，費用將高於實際交易金額，敬請知悉。 </>)}
            </span>
          </p>
        </div>
        <div className="guide-line">
          <p className="txt5">
            <span className="name3">{getText(language, '精算', 'Settlement', '結算')}</span>
            <span className="content">
              {getText(language, 'ETC料金、追加料金が発生した場合は、料金精算を実施させていただきます。'
                ,'If ETC fees or additional charges occur, we will settle the charges.'
                ,'若產生ETC費用 或其他追加服務，我們將收取現金結算。部份服務可信用卡支付。')}
            </span>
          </p>
        </div>
      </>
    );
  };

  return (
    <div className="guide-page">
      <div className="position">
        <span>{t('guide.position', language)}</span>
        <Link to="/">{t('common.daydaygo', language)}</Link>
        <span>{t('common.greaterThan', language)}{t('guide.guideTitle', language)}</span>
      </div>
      <div className="guide">
        <div className="left">
          <p className="name">{t('guide.otherGuides', language)}</p>
          <ul>
            {guideLinks.map((link) => (
              <li key={link.id}>
                <Link 
                  to={link.path}
                  className={link.active ? 'ok' : ''}
                  onClick={() => handleGuideClick(link.id)}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="right">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Guide;
